// components/Auth/RegisterForm.js - Упрощенная форма регистрации

import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { VALIDATION_RULES } from '../../types/api';
import PrivacyPolicyModal from '../common/PrivacyPolicyModal';
import '../../styles/Auth.css';

const RegisterForm = ({ onSwitchToLogin, onSuccess, onClose }) => {
  // =============================================================================
  // СОСТОЯНИЕ ФОРМЫ
  // =============================================================================
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  
  // =============================================================================
  // КОНТЕКСТ АУТЕНТИФИКАЦИИ
  // =============================================================================
  
  const { register, error: authError, isLoading } = useAuth();
  
  // =============================================================================
  // ОБРАБОТЧИКИ
  // =============================================================================
  
  /**
   * Обработчик изменения полей формы
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Очищаем ошибку для текущего поля
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Очищаем ошибку подтверждения пароля при изменении пароля
    if (name === 'password' && formErrors.confirmPassword) {
      setFormErrors(prev => ({
        ...prev,
        confirmPassword: ''
      }));
    }
  };
  
  /**
   * Валидация формы
   */
  const validateForm = () => {
    const errors = {};
    
    // Валидация email
    if (!formData.email.trim()) {
      errors.email = 'Введите email';
    } else if (!VALIDATION_RULES.EMAIL.pattern.test(formData.email)) {
      errors.email = 'Неверный формат email';
    }
    
    // Валидация пароля
    if (!formData.password) {
      errors.password = 'Введите пароль';
    } else if (formData.password.length < VALIDATION_RULES.PASSWORD.minLength) {
      errors.password = `Минимум ${VALIDATION_RULES.PASSWORD.minLength} символов`;
    }
    
    // Валидация подтверждения пароля
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Подтвердите пароль';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Пароли не совпадают';
    }

    if (!privacyConsent) {
      errors.privacyConsent = 'Необходимо согласие на обработку персональных данных';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  /**
   * Обработчик отправки формы
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Создаем имя пользователя из email
      const emailUsername = formData.email.split('@')[0];
      const fullName = emailUsername.charAt(0).toUpperCase() + emailUsername.slice(1);
      
      const result = await register({
        fullName: fullName,
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        confirmPassword: formData.confirmPassword
      });
      
      if (result.success && onSuccess) {
        onSuccess(result.user);
      }
    } catch (error) {
      console.error('Ошибка регистрации:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // =============================================================================
  // РЕНДЕР
  // =============================================================================
  
  return (
    <div className="auth-form-container">
      <div className="auth-form">
        <div className="auth-header">
          {onClose && (
            <button
              type="button"
              className="auth-close-btn"
              onClick={onClose}
              aria-label="Закрыть"
            >
              ×
            </button>
          )}
          <h2>Регистрация</h2>
        </div>
        
        <form onSubmit={handleSubmit} className="auth-form-body">
          {/* Поле Email */}
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              E-mail <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <div className="form-input-wrapper">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`form-input ${formErrors.email ? 'form-input-error' : ''}`}
                placeholder="E-mail"
                disabled={isLoading || isSubmitting}
                autoComplete="email"
              />
            </div>
            {formErrors.email && (
              <span className="form-error">{formErrors.email}</span>
            )}
          </div>
          
          {/* Поле Пароль */}
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Пароль <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <div className="form-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`form-input ${formErrors.password ? 'form-input-error' : ''}`}
                placeholder="Пароль"
                disabled={isLoading || isSubmitting}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {formErrors.password && (
              <span className="form-error">{formErrors.password}</span>
            )}
          </div>
          
          {/* Поле Подтверждение пароля */}
          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Повторите пароль <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <div className="form-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`form-input ${formErrors.confirmPassword ? 'form-input-error' : ''}`}
                placeholder="Повторите пароль"
                disabled={isLoading || isSubmitting}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Скрыть пароль' : 'Показать пароль'}
                tabIndex={-1}
              >
                {showConfirmPassword ? (
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {formErrors.confirmPassword && (
              <span className="form-error">{formErrors.confirmPassword}</span>
            )}
          </div>
          
          {/* Общая ошибка */}
          {authError && (
            <div className="auth-error">
              {authError}
            </div>
          )}
          
          {/* Согласие на обработку персональных данных */}
          <div className="auth-consent auth-consent--checkbox">
            <label className="auth-consent-label" htmlFor="privacy-consent">
              <input
                id="privacy-consent"
                type="checkbox"
                checked={privacyConsent}
                onChange={(e) => {
                  setPrivacyConsent(e.target.checked);
                  if (formErrors.privacyConsent) {
                    setFormErrors((prev) => ({ ...prev, privacyConsent: '' }));
                  }
                }}
                disabled={isLoading || isSubmitting}
                className="auth-consent-checkbox"
              />
              <span className="auth-consent-text">
                Я согласен(а) на обработку моих персональных данных в соответствии с{' '}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowPrivacyPolicy(true);
                  }}
                >
                  Политикой конфиденциальности
                </a>
                .
              </span>
            </label>
            {formErrors.privacyConsent && (
              <span className="form-error auth-consent-error">{formErrors.privacyConsent}</span>
            )}
          </div>
          
          {/* Модальное окно с политикой конфиденциальности */}
          <PrivacyPolicyModal 
            isOpen={showPrivacyPolicy} 
            onClose={() => setShowPrivacyPolicy(false)} 
          />
          
          {/* Кнопка отправки */}
          <button
            type="submit"
            className="auth-submit-btn"
            disabled={isLoading || isSubmitting || !privacyConsent}
          >
            {isLoading || isSubmitting ? (
              <>
                <span className="loading-spinner"></span>
                Регистрация...
              </>
            ) : (
              'Зарегистрироваться'
            )}
          </button>
        </form>
        
        {/* Футер формы */}
        <div className="auth-footer">
          <p>
            Есть аккаунт?{' '}
            <button
              type="button"
              className="auth-link-btn"
              onClick={onSwitchToLogin}
              disabled={isLoading || isSubmitting}
            >
              Войти
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;